// src/pages/shared/SupportPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useRedux';
import { createConversation, getSupportConversations, getConversation, Conversation } from '../../services/chatService';
import { useUI } from '../../context/UIContext';
import { ArrowBackIcon } from '../../components/common/Icons';
import TeslaButton from '../../components/common/TeslaButton';
import api from '../../api/apiClient';
import '../../styles/SupportPage.css';

/**
 * Fetch a support agent using the dedicated endpoint
 * Endpoint: GET /api/users/support-agent
 */
const getSupportAgent = async () => {
  try {
    const response = await api.get('/users/support-agent');
    if (response.data && response.data.data) {
      return response.data.data;
    } else {
      throw new Error('No support agent found. Please contact your administrator.');
    }
  } catch (error: any) {
    console.error('Error in getSupportAgent:', error);
    // Provide more helpful error messages
    if (error.response?.status === 404) {
      throw new Error('No support agent is currently available. Please try again later.');
    } else if (error.response?.status === 401) {
      throw new Error('You must be logged in to contact support.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to contact support.');
    }
    throw new Error(error.response?.data?.message || error.message || 'Failed to find support agent');
  }
};

const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const { setLoading: setUILoading, showSnackbar } = useUI();

  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [lastSender, setLastSender] = useState<Record<string, string>>({});

  const agentId = user?.id || (user as any)?._id;
  const isSupportAgent =
    user?.userType === 'admin' &&
    (user as any)?.adminRole === 'supportAgent';

  // Regular user flow: contact support
  const handleContactSupport = async () => {
    const userId = user?.id || (user as any)?._id;
    if (!userId) {
      showSnackbar('You must be logged in to contact support.', 'error');
      return;
    }
    try {
      setLoading(true);
      setUILoading(true);
      
      const supportAgent = await getSupportAgent();
      if (!supportAgent || !supportAgent._id) {
        showSnackbar('No support agent available at the moment.', 'error');
        setLoading(false);
        setUILoading(false);
        return;
      }
      
      const conversation = await createConversation([userId, supportAgent._id], 'support');
      
      setLoading(false);
      setUILoading(false);
      
      // Navigate to chat page with proper user type route (matching mobile implementation)
      const userType = user?.userType || 'client';
      navigate(`/${userType}/chat/${conversation._id}`);
      // Note: No snackbar needed - user is being redirected to chat window
    } catch (error: any) {
      setLoading(false);
      setUILoading(false);
      console.error('Error contacting support:', error);
      showSnackbar(error.message || 'Failed to contact support', 'error');
    }
  };

  // Support agent flow: fetch all support convos
  const fetchSupportDashboard = async () => {
    if (!agentId) return;
    try {
      setLoading(true);
      const convos = await getSupportConversations();
      setConversations(convos);

      const newUnread: Record<string, number> = {};
      const newLastSender: Record<string, string> = {};

      // For each conversation, fetch messages & compute unread + track last message sender
      for (const convo of convos) {
        const convoDetails = await getConversation(convo._id);
        const msgs = convoDetails.messages;
        let count = 0;

        for (const msg of msgs) {
          if (!msg.readBy.includes(agentId) && msg.sender !== agentId) {
            count++;
          }
        }
        newUnread[convo._id] = count;

        // Determine the last message sender
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          newLastSender[convo._id] = lastMsg.sender;
        } else {
          newLastSender[convo._id] = '';
        }
      }

      setUnreadCounts(newUnread);
      setLastSender(newLastSender);
    } catch (err) {
      console.error('Error fetching support dashboard:', err);
      showSnackbar('Failed to fetch support conversations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSupportAgent && agentId) {
      fetchSupportDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupportAgent, agentId]);

  // Render conversation card
  const renderConversationItem = (convo: Conversation) => {
    // Find the other participant (the user, not the agent)
    const otherParticipant = convo.participants?.find((p: any) => {
      const id = typeof p === 'string' ? p : (p?._id || p);
      return id !== agentId;
    });
    
    let roleLabel = 'Unknown';
    let participantName = 'Unknown User';
    
    if (otherParticipant) {
      if (typeof otherParticipant === 'object') {
        const participant = otherParticipant as any;
        if (participant.userType === 'client') {
          roleLabel = 'Client';
        } else if (participant.userType === 'stylist') {
          roleLabel = 'Stylist';
        }
        participantName = participant.name || 'Unknown User';
      }
    }
    
    const participantLabel = `${roleLabel} ${participantName}`;

    const unreadCount = unreadCounts[convo._id] || 0;
    const lastMsgSender = lastSender[convo._id] || '';

    // Decide which badge to show
    let badge = null;
    if (unreadCount > 0) {
      badge = (
        <div className="support-badge support-badge--unread">
          <span className="support-badge__icon">✉️</span>
          <span className="support-badge__text">{unreadCount}</span>
        </div>
      );
    } else if (lastMsgSender && lastMsgSender !== agentId) {
      badge = (
        <div className="support-badge support-badge--reply">
          <span className="support-badge__icon">💬</span>
          <span className="support-badge__text">Reply</span>
        </div>
      );
    }

    return (
      <div
        key={convo._id}
        className="support-conversation-card"
        onClick={() => {
          const userType = user?.userType || 'client';
          navigate(`/${userType}/chat/${convo._id}`);
        }}
      >
        <div className="support-conversation-card__header">
          <h3 className="support-conversation-card__title">{participantLabel}</h3>
          {badge}
        </div>
        <div className="support-conversation-card__divider"></div>
        <div className="support-conversation-card__footer">
          <span className="support-conversation-card__type">Support Chat</span>
          <span className="support-conversation-card__arrow">→</span>
        </div>
      </div>
    );
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="support-page">
        <div className="support-page__loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (isSupportAgent) {
    // Show support dashboard
    return (
      <div className="support-page">
        <div className="support-header">
          <button className="support-back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </button>
          <h1 className="support-title">Support Dashboard</h1>
        </div>

        <div className="support-content">
          {conversations.length === 0 ? (
            <div className="support-empty">
              <p>No support conversations found.</p>
            </div>
          ) : (
            <div className="support-conversations">
              {conversations.map(renderConversationItem)}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Otherwise, normal user: contact support
  return (
    <div className="support-page">
      <div className="support-header">
        <button className="support-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className="support-title">Support</h1>
      </div>

      <div className="support-content">
        <div className="tesla-card">
          <div className="tesla-card__content" style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="support-icon" style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>
              💬
            </div>
            <h2 className="support-heading">Need Help?</h2>
            <p className="support-description">
              Tap the button below to start a chat with our support team. 
              We're here to help you with any questions or issues you may have.
            </p>
            <TeslaButton
              variant="primary"
              size="lg"
              onClick={handleContactSupport}
              disabled={loading}
              style={{ marginTop: '2rem' }}
            >
              {loading ? 'Connecting...' : 'Contact Support'}
            </TeslaButton>
          </div>
        </div>

        <div className="tesla-card">
          <div className="tesla-card__header">
            <h2 className="tesla-card__title">Frequently Asked Questions</h2>
          </div>
          <div className="tesla-card__content">
            <div className="faq-item">
              <h3 className="faq-question">How do I book an appointment?</h3>
              <p className="faq-answer">
                Browse stylists, select a service, choose a date and time, then complete your booking.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Can I cancel or reschedule?</h3>
              <p className="faq-answer">
                Yes, you can cancel or reschedule appointments from your appointments page up to 24 hours before the scheduled time.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">How do I update my profile?</h3>
              <p className="faq-answer">
                Go to your profile page and click "Edit Profile" to update your information.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;

